/**
 * Lógica pura del comparador de inteligencia (Sprint 286 D; REQ-MINT-006 §1.2).
 *
 * - Presets de pesos (D7): equilibrado/capaz/barato/rápido.
 * - Puntos precio–calidad y **frontera de Pareto** (no dominados): **precio
 *   cliente** (USD/1M entrada, calculado en backend y solo con permiso de
 *   precio) frente al score ponderado. Sin precio o sin score, el punto no
 *   entra en la frontera y nunca domina a otros (no se estima nada).
 */

import type { OfferingIntelligence } from '../types';

export interface WeightPreset {
  id: 'balanced' | 'capable' | 'cheaper' | 'faster';
  weights: Record<string, number>;
}

/** Presets de pesos (calidad/coste/latencia; D7). */
export const WEIGHT_PRESETS: WeightPreset[] = [
  { id: 'balanced', weights: { quality: 0.5, cost: 0.3, latency: 0.2 } },
  { id: 'capable', weights: { quality: 0.6, cost: 0.2, latency: 0.2 } },
  { id: 'cheaper', weights: { quality: 0.3, cost: 0.6, latency: 0.1 } },
  { id: 'faster', weights: { quality: 0.3, cost: 0.1, latency: 0.6 } },
];

export interface ComparatorPoint {
  key: string;
  label: string;
  provider: string;
  modelRef: string | null;
  score: number | null;
  priceUsdPerMillionIn: number | null;
}

/** Extrae los puntos coste–calidad de los elegibles (nunca inventa cifras). */
export function comparatorPoints(eligible: OfferingIntelligence[]): ComparatorPoint[] {
  return eligible.map((item) => {
    const priceIn = item.client_price?.components?.['input']?.usd_per_million;
    const priceUsdPerMillionIn =
      item.client_price?.state === 'available' && typeof priceIn === 'number'
        ? priceIn
        : null;
    return {
      key: item.offering.sheet_id,
      label: item.offering.model_ref ?? item.offering.provider_model_id,
      provider: item.offering.provider,
      modelRef: item.offering.model_ref ?? null,
      score: item.scores?.weighted_score ?? null,
      priceUsdPerMillionIn,
    };
  });
}

/**
 * Frontera de Pareto: claves de los puntos no dominados (mejor score y menor
 * precio a la vez). Solo se consideran puntos con precio y score conocidos.
 */
export function frontierKeys(points: ComparatorPoint[]): string[] {
  const usable = points.filter(
    (point) => point.score !== null && point.priceUsdPerMillionIn !== null,
  );
  const frontier: string[] = [];
  for (const candidate of usable) {
    const score = candidate.score as number;
    const price = candidate.priceUsdPerMillionIn as number;
    const dominated = usable.some((other) => {
      if (other.key === candidate.key) return false;
      const otherScore = other.score as number;
      const otherPrice = other.priceUsdPerMillionIn as number;
      const noWorse = otherScore >= score && otherPrice <= price;
      const strictlyBetter = otherScore > score || otherPrice < price;
      return noWorse && strictlyBetter;
    });
    if (!dominated) frontier.push(candidate.key);
  }
  return frontier;
}
